import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useGems } from '@/hooks/useGems';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

interface UseIcebreakerSuggestionsReturn {
  suggestions: string[];
  isLoading: boolean;
  error: string | null;
  generateSuggestions: (partnerId: string) => Promise<boolean>;
  clearSuggestions: () => void;
}

export function useIcebreakerSuggestions(): UseIcebreakerSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refreshGems } = useGems();

  const generateSuggestions = useCallback(async (partnerId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      debugLogger.debug('Icebreaker', 'Generating suggestions for partner:', partnerId);

      const { data, error: functionError } = await supabase.functions.invoke('generate-icebreaker', {
        body: { partnerId }
      });

      if (functionError) {
        debugLogger.error('Icebreaker', 'Function error:', functionError);
        throw new Error(functionError.message || 'Erro ao gerar sugestões');
      }

      if (data?.error) {
        debugLogger.error('Icebreaker', 'API error:', data.error, data.code);
        
        if (data.code === 'INSUFFICIENT_GEMS') {
          setError('Gemas insuficientes');
          toast.error('Você não tem gemas suficientes. Ganhe gemas na Roleta da Sorte!');
          return false;
        }
        
        if (data.code === 'RATE_LIMIT') {
          setError('Muitas requisições');
          toast.error('Aguarde alguns segundos antes de tentar novamente.');
          return false;
        }

        throw new Error(data.error);
      }

      if (!data?.suggestions || data.suggestions.length === 0) {
        throw new Error('Nenhuma sugestão gerada');
      }

      debugLogger.debug('Icebreaker', 'Generated suggestions:', data.suggestions);
      setSuggestions(data.suggestions);
      
      // Refresh gems balance after successful generation
      await refreshGems();
      
      toast.success('Sugestões geradas com sucesso!');
      return true;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar sugestões';
      debugLogger.error('Icebreaker', 'Error:', message);
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [refreshGems]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    generateSuggestions,
    clearSuggestions
  };
}
